import { useCallback, useState } from 'react';
import { NormalFormSelectionField, NormalFormTextField } from '../molecules';
import { ContractStatusOption, OrderDirectionOption, OwnerContractFilterInput, OwnerContractLeaseTypeOption, PageSizeOption } from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import { contractStatusMapper, orderDirectionMapper, ownerContractLeaseTypeMapper, pageSizeMapper } from '@/helpers';

export type OwnerContractFiltersProps = {
  filters: OwnerContractFilterInput;
  onFilterChange: (newFilters: OwnerContractFilterInput) => void;
};

const initialOwnerContractFilters: OwnerContractFilterInput = {
  status: null,
  leaseType: null,
  orderDirection: OrderDirectionOption.Desc,
  search: null,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
  startMonth: null,
  endMonth: null,
};

const textsData = {
  en: {
    filtersTitle: 'Filters',
    searchLabel: 'Search',
    searchPlaceholder: 'Search owner contracts...',
    statusLabel: 'Contract Status',
    statusNoValueText: 'All',
    leaseTypeLabel: 'Lease Type',
    leaseTypeNoValueText: 'All',
    orderLabel: 'Order',
    pageSizeLabel: 'Records per page',
    pageSizeNoValueText: 'All',
    startMonthLabel: 'Start Month',
    endMonthLabel: 'End Month',
    monthNoValueText: 'All',
    clearFiltersButton: 'Clear Filters',
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },
  },
  es: {
    filtersTitle: 'Filtros',
    searchLabel: 'Buscar',
    searchPlaceholder: 'Buscar contratos...',
    statusLabel: 'Estado del Contrato',
    statusNoValueText: 'Todos',
    leaseTypeLabel: 'Tipo de Contrato',
    leaseTypeNoValueText: 'Todos',
    orderLabel: 'Orden',
    pageSizeLabel: 'Registros por página',
    pageSizeNoValueText: 'Todos',
    startMonthLabel: 'Mes de Inicio',
    endMonthLabel: 'Mes de Fin',
    monthNoValueText: 'Todos',
    clearFiltersButton: 'Limpiar Filtros',
    months: {
      january: 'Enero',
      february: 'Febrero',
      march: 'Marzo',
      april: 'Abril',
      may: 'Mayo',
      june: 'Junio',
      july: 'Julio',
      august: 'Agosto',
      september: 'Septiembre',
      october: 'Octubre',
      november: 'Noviembre',
      december: 'Diciembre',
    },
  },
};

export const OwnerContractFilters = ({ filters, onFilterChange }: OwnerContractFiltersProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const [isOpen, setIsOpen] = useState(false);

  const handleFieldChange = useCallback((field: keyof OwnerContractFilterInput, value: string | boolean | number | null) => {
    onFilterChange({
      ...filters,
      [field]: value === '' ? null : value,
    });
  }, [filters, onFilterChange]);

  const statusOptions = Object.values(ContractStatusOption).map(value => ({
    name: contractStatusMapper(value, userLanguage),
    value: value,
  }));

  const leaseTypeOptions = Object.values(OwnerContractLeaseTypeOption).map(value => ({
    name: ownerContractLeaseTypeMapper(value, userLanguage),
    value: value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map(value => ({
    name: orderDirectionMapper(value, userLanguage),
    value: value,
  }));

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map(value => ({
    name: pageSizeMapper(value, userLanguage),
    value: value,
  }));

  const monthOptions = [
    { name: texts.months.january, value: '1' },
    { name: texts.months.february, value: '2' },
    { name: texts.months.march, value: '3' },
    { name: texts.months.april, value: '4' },
    { name: texts.months.may, value: '5' },
    { name: texts.months.june, value: '6' },
    { name: texts.months.july, value: '7' },
    { name: texts.months.august, value: '8' },
    { name: texts.months.september, value: '9' },
    { name: texts.months.october, value: '10' },
    { name: texts.months.november, value: '11' },
    { name: texts.months.december, value: '12' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4"
      >
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M3 4h18M6 12h12M10 20h4"
            />
          </svg>
          <span className="font-semibold text-gray-700">
            {texts.filtersTitle}
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""
            }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      <div
        className={`transition-all duration-300 overflow-hidden px-6 ${isOpen ? "max-h-[600px] opacity-100 py-4" : "max-h-0 opacity-0"}`}
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <NormalFormTextField
              label={texts.searchLabel}
              name="search"
              placeholder={texts.searchPlaceholder}
              value={filters.search || ''}
              onChange={(value) => handleFieldChange('search', value)}
            />
          </div>
          <div className="md:col-span-3">
            <NormalFormSelectionField<ContractStatusOption>
              label={texts.statusLabel}
              name="status"
              options={statusOptions}
              value={filters.status}
              onChange={(value) => handleFieldChange('status', value)}
              noValueText={texts.statusNoValueText}
            />
          </div>
          <div className="md:col-span-4">
            <NormalFormSelectionField<OwnerContractLeaseTypeOption>
              label={texts.leaseTypeLabel}
              name="leaseType"
              options={leaseTypeOptions}
              value={filters.leaseType}
              onChange={(value) => handleFieldChange('leaseType', value)}
              noValueText={texts.leaseTypeNoValueText}
            />
          </div>
        </div>
        <div className="border-t border-gray-100"></div>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-2">
            <NormalFormSelectionField<OrderDirectionOption>
              label={texts.orderLabel}
              name="orderDirection"
              options={orderDirectionOptions}
              value={filters.orderDirection}
              onChange={(value) => handleFieldChange('orderDirection', value)}
            />
          </div>
          <div className="md:col-span-2">
            <NormalFormSelectionField<PageSizeOption>
              label={texts.pageSizeLabel}
              name="pageSize"
              options={pageSizeOptions}
              value={filters.pageSize}
              onChange={(value) => handleFieldChange('pageSize', value)}
              noValueText={texts.pageSizeNoValueText}
            />
          </div>
          <div className="md:col-span-2">
            <NormalFormSelectionField<string>
              label={texts.startMonthLabel}
              name="startMonth"
              options={monthOptions}
              value={filters.startMonth}
              onChange={(value) => handleFieldChange('startMonth', value)}
              noValueText={texts.monthNoValueText}
            />
          </div>
          <div className="md:col-span-2">
            <NormalFormSelectionField<string>
              label={texts.endMonthLabel}
              name="endMonth"
              options={monthOptions}
              value={filters.endMonth}
              onChange={(value) => handleFieldChange('endMonth', value)}
              noValueText={texts.monthNoValueText}
            />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button
              onClick={() => onFilterChange(initialOwnerContractFilters)}
              className="
              px-6 py-2.5
              rounded-xl
              bg-color-background2
              text-color-primary1
              font-medium
              hover:bg-gray-200
              transition
              shadow-sm
            "
            >
              {texts.clearFiltersButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
